import { DialogComponent } from '@theme/dialog';

const REVIEWS_PER_PAGE = 25;
const DEFAULT_OKENDO_USER_ID = '52d80c48-bbee-4f58-9df7-fe59c69e448f';
const REVIEW_ATTRIBUTE_ORDER = ['quality', 'design', 'sizing', 'fit'];

/**
 * Custom PDP reviews powered by Okendo Storefront API.
 *
 * @extends {DialogComponent}
 */
class ProductReviewsComponent extends DialogComponent {
  /** @type {string | null} */
  #nextUrl = null;

  /** @type {string} */
  #verifiedIconHtml = '';

  /** @type {boolean} */
  #panelLoaded = false;

  /** @type {boolean} */
  #filtersLoaded = false;

  /** @type {boolean} */
  #loadingFilters = false;

  /** @type {boolean} */
  #loadingMore = false;

  /** @type {boolean} */
  #loadingReviews = false;

  /** @type {string} */
  #apiBase = '';

  /** @type {Array<Record<string, unknown>>} */
  #allReviews = [];

  /** @type {{ ratingOptions: Array<{ value: number; count: number }>; attributeOptions: Array<{ title: string; values: Array<{ value: string; count: number }> }>; keywordOptions: Array<{ value: string; count: number; topic?: string }> } | null} */
  #filterOptionsData = null;

  /** @type {Record<string, unknown> | null} */
  #aggregate = null;

  #filters = {
    sort: 'date desc',
    rating: null,
    shopperAttribute: null,
    keyword: null,
  };

  connectedCallback() {
    super.connectedCallback();
    const iconHost = this.querySelector('[data-verified-icon]');
    if (iconHost) {
      this.#verifiedIconHtml = iconHost.innerHTML.trim();
    }
    this.#apiBase = this.#resolveApiBase();
    this.#loadSummary();
  }

  /**
   * Opens the full reviews panel.
   */
  openPanel() {
    this.showDialog();

    if (!this.#panelLoaded) {
      this.#loadPanel();
    }
  }

  /**
   * Loads the next page of reviews.
   */
  async loadMore() {
    if (!this.#nextUrl || this.#loadingMore || this.#loadingReviews) return;

    this.#loadingMore = true;
    const { loadMoreButton } = this.refs;
    const buttonLabel = loadMoreButton instanceof HTMLButtonElement ? loadMoreButton.textContent : '';

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.disabled = true;
      loadMoreButton.textContent = 'Loading…';
    }

    try {
      const data = await this.#fetchUrl(this.#resolveNextUrl(this.#nextUrl));
      const reviews = data?.reviews ?? [];

      if (reviews.length === 0) {
        this.#nextUrl = null;
        this.#updateLoadMore();
        return;
      }

      this.#appendReviews(reviews);
      this.#nextUrl = this.#extractNextUrl(data);
      this.#updateLoadMore();
    } catch (error) {
      console.error('Product reviews: failed to load more', error);
    } finally {
      this.#loadingMore = false;

      if (loadMoreButton instanceof HTMLButtonElement) {
        loadMoreButton.disabled = !this.#nextUrl;
        loadMoreButton.textContent = buttonLabel || 'Load more';
      }
    }
  }

  toggleFilters() {
    const { filtersPanel, filtersToggle } = this.refs;
    if (!(filtersPanel instanceof HTMLElement)) return;

    const isOpen = filtersPanel.hidden === false;

    filtersPanel.hidden = isOpen;
    filtersPanel.toggleAttribute('hidden', isOpen);

    if (filtersToggle instanceof HTMLElement) {
      filtersToggle.setAttribute('aria-expanded', String(!isOpen));
    }

    if (isOpen) return;

    if (!this.#filtersLoaded && !this.#loadingFilters) {
      this.#loadFilters();
    }
  }

  async #loadFilters() {
    const userId = this.#getUserId();
    const productId = this.#getProductId();
    const { filterOptions } = this.refs;

    if (!userId || !productId || !(filterOptions instanceof HTMLElement)) return;

    this.#loadingFilters = true;
    filterOptions.innerHTML = '<p class="product-reviews__filters-loading">Loading filters…</p>';

    try {
      let aggregate = this.#aggregate;

      if (!aggregate) {
        const aggregateData = await this.#fetchPath(userId, productId, 'review_aggregate').catch(() => null);
        aggregate = this.#extractAggregate(aggregateData);
        this.#aggregate = aggregate;
      }

      const keywordsData = await this.#fetchPath(userId, productId, 'reviews_keywords').catch(() => null);
      this.#filterOptionsData = this.#extractFilterOptions(aggregate, keywordsData);
      this.#filtersLoaded = true;
      this.#renderFilterOptions(this.#filterOptionsData);
    } catch (error) {
      console.error('Product reviews: failed to load filters', error);
      filterOptions.innerHTML = '<p class="product-reviews__filters-loading">Filters unavailable right now.</p>';
    } finally {
      this.#loadingFilters = false;
    }
  }

  handleSortChange() {
    const { sortSelect } = this.refs;

    this.#filters.sort = sortSelect instanceof HTMLSelectElement ? sortSelect.value : 'date desc';
    this.#applyFilters();
  }

  /**
   * @param {Event} event
   */
  handleFilterChipClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-filter-type]');
    if (!(button instanceof HTMLButtonElement)) return;

    const type = button.dataset.filterType;
    const value = button.dataset.filterValue ?? '';
    const attributeTitle = button.dataset.filterAttribute ?? '';

    switch (type) {
      case 'rating': {
        const rating = Number(value);
        this.#filters.rating = this.#filters.rating === rating ? null : rating;
        break;
      }
      case 'keyword':
        this.#filters.keyword = this.#filters.keyword === value ? null : value;
        break;
      case 'attribute':
        if (!attributeTitle) break;

        if (
          this.#filters.shopperAttribute?.title === attributeTitle
          && this.#filters.shopperAttribute?.value === value
        ) {
          this.#filters.shopperAttribute = null;
        } else {
          this.#filters.shopperAttribute = { title: attributeTitle, value };
        }
        break;
      default:
        break;
    }

    this.#applyFilters();
  }

  clearFilters() {
    const { sortSelect } = this.refs;

    this.#filters = {
      sort: 'date desc',
      rating: null,
      shopperAttribute: null,
      keyword: null,
    };

    if (sortSelect instanceof HTMLSelectElement) {
      sortSelect.value = 'date desc';
    }

    this.#applyFilters();
  }

  async #loadSummary() {
    const userId = this.#getUserId();
    const productId = this.#getProductId();
    const fallbackSummary = this.#buildFallbackSummary();

    if (!userId || !productId) {
      this.#setSummaryText(fallbackSummary);
      this.#setSummaryRecommend(this.#buildFallbackRecommendText());
      return;
    }

    try {
      const [summaryData, aggregateData] = await Promise.all([
        this.#fetchPath(userId, productId, 'reviews_summary').catch(() => null),
        this.#fetchPath(userId, productId, 'review_aggregate').catch(() => null),
      ]);
      const summaryText = this.#extractSummaryText(summaryData) || fallbackSummary;
      this.#setSummaryText(summaryText);
      const aggregate = this.#extractAggregate(aggregateData);
      this.#setSummaryRecommend(this.#buildRecommendText(aggregate));
    } catch (error) {
      console.warn('Product reviews: summary unavailable', error);
      this.#setSummaryText(fallbackSummary);
      this.#setSummaryRecommend(this.#buildFallbackRecommendText());
    }
  }

  async #loadPanel() {
    const userId = this.#getUserId();
    const productId = this.#getProductId();
    const { panelLoading, panelContent, panelError } = this.refs;

    if (!userId || !productId) {
      console.error('Product reviews: missing Okendo user ID or product ID', {
        userId,
        productId,
      });
      this.#showPanelError();
      return;
    }

    if (panelLoading instanceof HTMLElement) panelLoading.hidden = false;
    if (panelContent instanceof HTMLElement) panelContent.hidden = true;
    if (panelError instanceof HTMLElement) panelError.hidden = true;

    try {
      const [aggregateData, reviewsData] = await Promise.all([
        this.#fetchPath(userId, productId, 'review_aggregate').catch(() => null),
        this.#fetchPath(userId, productId, this.#buildReviewsQuery()).catch(() => null),
      ]);

      if (!reviewsData) {
        throw new Error('Reviews list unavailable');
      }

      const aggregate = this.#extractAggregate(aggregateData);
      this.#aggregate = aggregate;

      this.#renderAggregate(aggregate);
      this.#renderAttributes(aggregate);
      this.#renderReviews(reviewsData?.reviews ?? []);

      this.#nextUrl = this.#extractNextUrl(reviewsData);
      this.#updateLoadMore();
      this.#updateFiltersButton();

      this.#panelLoaded = true;

      if (panelLoading instanceof HTMLElement) panelLoading.hidden = true;
      if (panelContent instanceof HTMLElement) panelContent.hidden = false;
    } catch (error) {
      console.error('Product reviews: panel failed to load', error);
      this.#showPanelError();
    }
  }

  async #applyFilters() {
    if (this.#loadingReviews || this.#loadingMore) return;

    const userId = this.#getUserId();
    const productId = this.#getProductId();
    if (!userId || !productId) return;

    this.#loadingReviews = true;
    const { reviewsList, loadMoreButton } = this.refs;

    if (reviewsList instanceof HTMLElement) {
      reviewsList.setAttribute('aria-busy', 'true');
    }

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.disabled = true;
    }

    try {
      const data = await this.#fetchPath(userId, productId, this.#buildReviewsQuery());
      const reviews = data?.reviews ?? [];

      this.#renderReviews(reviews);
      this.#nextUrl = this.#extractNextUrl(data);
      this.#updateLoadMore();
      this.#updateFiltersButton();

      if (this.#filterOptionsData) {
        this.#renderFilterOptions(this.#filterOptionsData);
      }
    } catch (error) {
      console.error('Product reviews: failed to apply filters', error);
    } finally {
      this.#loadingReviews = false;

      if (reviewsList instanceof HTMLElement) {
        reviewsList.removeAttribute('aria-busy');
      }

      this.#updateLoadMore();
    }
  }

  #buildReviewsQuery() {
    const params = new URLSearchParams();
    params.set('limit', String(REVIEWS_PER_PAGE));
    params.set('orderBy', this.#filters.sort);

    const locale = this.#getLocale();
    if (locale) {
      params.set('locale', locale);
    }

    // Undocumented widget params — not in public API docs but used by Okendo's own widget.
    if (this.#filters.rating) {
      params.set('ratings', String(this.#filters.rating));
    }

    if (this.#filters.shopperAttribute) {
      const { title, value } = this.#filters.shopperAttribute;
      params.set('shopperAttributes', `${title},${value}`);
    }

    if (this.#filters.keyword) {
      params.set('keywords', this.#filters.keyword);
    }

    return `reviews?${params.toString()}`;
  }

  #getLocale() {
    const raw = this.dataset.locale?.trim() || document.documentElement.lang || 'en';
    return this.#mapOkendoLocale(raw);
  }

  /**
   * @param {string} locale
   */
  #mapOkendoLocale(locale) {
    const normalized = locale.trim().replace('_', '-');

    const aliases = {
      'zh': 'zh-TW',
      'zh-cn': 'zh-TW',
      'pt': 'pt-PT',
      'de': 'de-formal',
    };

    const supported = new Set([
      'zh-TW',
      'da',
      'nl',
      'en',
      'fr',
      'de-formal',
      'de-informal',
      'id',
      'it',
      'ja',
      'ko',
      'pl',
      'pt-PT',
      'ru',
      'es',
      'sv',
      'th',
      'vi',
    ]);

    const lower = normalized.toLowerCase();

    if (supported.has(normalized)) return normalized;
    if (aliases[lower]) return aliases[lower];

    const base = lower.split('-')[0];
    if (supported.has(base)) return base;
    if (aliases[base]) return aliases[base];

    return 'en';
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   * @param {unknown} keywordsData
   */
  #extractFilterOptions(aggregate, keywordsData) {
    return {
      ratingOptions: this.#extractRatingOptions(aggregate),
      attributeOptions: this.#extractShopperAttributeOptions(aggregate),
      keywordOptions: this.#extractKeywordOptions(keywordsData),
    };
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #extractRatingOptions(aggregate) {
    const byLevel = aggregate?.ratingAndReviewCountByLevel;

    if (!byLevel || typeof byLevel !== 'object') {
      return [];
    }

    const levels = /** @type {Record<string, number>} */ (byLevel);

    return [5, 4, 3, 2, 1]
      .map((level) => ({
        value: level,
        count: Number(levels[`level${level}Count`] ?? 0),
      }))
      .filter((option) => option.count > 0);
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #extractShopperAttributeOptions(aggregate) {
    if (!aggregate) return [];

    const sources = [
      aggregate.shopperAttributeBreakdown,
      aggregate.shopperAttributes,
      aggregate.filterableShopperAttributes,
      aggregate.reviewerAttributeBreakdown,
      aggregate.attributeBreakdown,
    ];

    for (const source of sources) {
      if (!Array.isArray(source) || source.length === 0) continue;

      const groups = source
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return null;

          const group = /** @type {Record<string, unknown>} */ (entry);
          const title = String(group.title ?? group.name ?? group.label ?? '').trim();
          const rawValues = group.values ?? group.options ?? group.breakdown ?? group.items ?? [];

          if (!title || !Array.isArray(rawValues)) return null;

          const values = rawValues
            .map((valueEntry) => {
              if (!valueEntry || typeof valueEntry !== 'object') return null;

              const item = /** @type {Record<string, unknown>} */ (valueEntry);
              const value = String(item.value ?? item.label ?? item.name ?? item.valueLabel ?? '').trim();
              const count = Number(item.count ?? item.reviewCount ?? item.total ?? 0);

              if (!value || count <= 0) return null;

              return { value, count };
            })
            .filter(Boolean);

          if (!values.length) return null;

          return { title, values: /** @type {Array<{ value: string; count: number }>} */ (values) };
        })
        .filter(Boolean);

      if (groups.length) {
        return this.#sortReviewAttributes(
          /** @type {Array<{ title: string; values: Array<{ value: string; count: number }> }>} */ (groups)
        );
      }
    }

    return [];
  }

  /**
   * @param {unknown} data
   */
  #extractKeywordOptions(data) {
    if (!data || typeof data !== 'object') return [];

    const record = /** @type {Record<string, unknown>} */ (data);
    const reviewsKeywords = record.reviewsKeywords;

    if (reviewsKeywords && typeof reviewsKeywords === 'object') {
      const keywordTopics = /** @type {{ keywordTopics?: unknown[] }} */ (reviewsKeywords).keywordTopics ?? [];

      if (Array.isArray(keywordTopics)) {
        return keywordTopics
          .flatMap((topicEntry) => {
            if (!topicEntry || typeof topicEntry !== 'object') return [];

            const topic = /** @type {Record<string, unknown>} */ (topicEntry);
            const topicName = String(topic.topic ?? '').trim();
            const keywords = Array.isArray(topic.keywords) ? topic.keywords : [];

            return keywords
              .map((keywordEntry) => {
                if (!keywordEntry || typeof keywordEntry !== 'object') return null;

                const keyword = /** @type {Record<string, unknown>} */ (keywordEntry);
                const name = String(keyword.name ?? '').trim();
                const count = Number(keyword.occurrences ?? 0);

                if (!name) return null;

                return {
                  value: name,
                  count,
                  topic: topicName,
                };
              })
              .filter(Boolean);
          })
          .slice(0, 16);
      }
    }

    const keywords = record.keywords ?? record.reviewKeywords ?? record.items ?? record.data ?? [];

    if (!Array.isArray(keywords)) return [];

    return keywords
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry.trim() ? { value: entry.trim(), count: 0, topic: '' } : null;
        }

        if (!entry || typeof entry !== 'object') return null;

        const item = /** @type {Record<string, unknown>} */ (entry);
        const value = String(item.keyword ?? item.text ?? item.value ?? item.name ?? '').trim();
        const count = Number(item.count ?? item.reviewCount ?? item.total ?? item.occurrences ?? 0);

        if (!value) return null;

        return { value, count, topic: '' };
      })
      .filter(Boolean)
      .slice(0, 16);
  }

  /**
   * @param {{ ratingOptions: Array<{ value: number; count: number }>; attributeOptions: Array<{ title: string; values: Array<{ value: string; count: number }> }>; keywordOptions: Array<{ value: string; count: number }> }} options
   */
  #renderFilterOptions(options) {
    const { filterOptions } = this.refs;
    if (!(filterOptions instanceof HTMLElement)) return;

    const sections = [];

    if (options.keywordOptions.length) {
      const keywordsByTopic = new Map();

      for (const keyword of options.keywordOptions) {
        const topic = String(keyword.topic ?? '').trim() || 'Keywords';
        const group = keywordsByTopic.get(topic) ?? [];
        group.push(keyword);
        keywordsByTopic.set(topic, group);
      }

      for (const [topic, keywords] of keywordsByTopic) {
        sections.push(
          this.#renderFilterSection(
            topic,
            keywords.map((keyword) => ({
              type: 'keyword',
              label: keyword.count ? `${keyword.value} (${keyword.count})` : keyword.value,
              value: keyword.value,
              active: this.#filters.keyword === keyword.value,
            }))
          )
        );
      }
    }

    if (options.ratingOptions.length) {
      sections.push(
        this.#renderFilterSection(
          'Rating',
          options.ratingOptions.map((rating) => ({
            type: 'rating',
            label: `${rating.value}★ (${rating.count})`,
            value: String(rating.value),
            active: this.#filters.rating === rating.value,
          }))
        )
      );
    }

    for (const group of options.attributeOptions) {
      sections.push(
        this.#renderFilterSection(
          group.title,
          group.values.map((value) => ({
            type: 'attribute',
            label: value.count ? `${value.value} (${value.count})` : value.value,
            value: value.value,
            attributeTitle: group.title,
            active:
              this.#filters.shopperAttribute?.title === group.title
              && this.#filters.shopperAttribute?.value === value.value,
          }))
        )
      );
    }

    filterOptions.innerHTML = sections.length
      ? sections.filter(Boolean).join('')
      : '<p class="product-reviews__filters-loading">No filters available for this product.</p>';

    const { filtersToggle } = this.refs;
    if (filtersToggle instanceof HTMLElement) {
      filtersToggle.hidden = sections.length === 0;
    }
  }

  /**
   * @param {string} title
   * @param {Array<{ type: string; label: string; value: string; active: boolean; attributeTitle?: string }>} chips
   */
  #renderFilterSection(title, chips) {
    if (!chips.length) return '';

    return `
      <div class="product-reviews__filter-group">
        <p class="product-reviews__filter-group-title">${this.#escapeHtml(title)}</p>
        <div class="product-reviews__filter-chips">
          ${chips
            .map(
              (chip) => `
                <button
                  type="button"
                  class="product-reviews__filter-chip${chip.active ? ' is-active' : ''}"
                  data-filter-type="${this.#escapeHtml(chip.type)}"
                  data-filter-value="${this.#escapeHtml(chip.value)}"
                  ${chip.attributeTitle ? `data-filter-attribute="${this.#escapeHtml(chip.attributeTitle)}"` : ''}
                  on:click="/handleFilterChipClick"
                  aria-pressed="${chip.active ? 'true' : 'false'}"
                >${this.#escapeHtml(chip.label)}</button>
              `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  #getUserId() {
    const userId = this.dataset.okendoUserId?.trim();
    return userId || DEFAULT_OKENDO_USER_ID;
  }

  #getProductId() {
    const rawId = this.dataset.productId?.trim();
    if (!rawId) return '';

    if (rawId.startsWith('gid://shopify/Product/')) {
      return rawId.replace('gid://shopify/Product/', '');
    }

    if (rawId.startsWith('shopify-')) {
      return rawId.replace('shopify-', '');
    }

    return rawId;
  }

  #resolveApiBase() {
    const userId = this.#getUserId();
    return `https://api.okendo.io/v1/stores/${userId}`;
  }

  /**
   * Okendo pagination: append nextUrl to the store base path.
   * @param {string} nextUrl
   */
  #resolveNextUrl(nextUrl) {
    if (!nextUrl) return '';

    const trimmed = nextUrl.trim();

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }

    if (trimmed.startsWith('/v1/')) {
      return `https://api.okendo.io${trimmed}`;
    }

    // Okendo pagination returns paths like /stores/{id}/products/shopify-{id}/reviews?...
    if (trimmed.startsWith('/stores/')) {
      return `https://api.okendo.io/v1${trimmed}`;
    }

    const productId = this.#getProductId();
    const productReviewsBase = `${this.#apiBase}/products/shopify-${productId}/reviews`;

    if (trimmed.startsWith('/products/')) {
      return `${this.#apiBase}${trimmed}`;
    }

    if (trimmed.startsWith('products/')) {
      return `${this.#apiBase}/${trimmed}`;
    }

    if (trimmed.startsWith('/reviews')) {
      return `${productReviewsBase}${trimmed.slice('/reviews'.length)}`;
    }

    if (trimmed.startsWith('reviews')) {
      return `${productReviewsBase}${trimmed.slice('reviews'.length)}`;
    }

    if (trimmed.startsWith('?')) {
      return `${productReviewsBase}${trimmed}`;
    }

    return `${this.#apiBase}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
  }

  /**
   * @param {unknown} data
   */
  #extractNextUrl(data) {
    if (!data || typeof data !== 'object') return null;

    const nextUrl = /** @type {Record<string, unknown>} */ (data).nextUrl;
    return typeof nextUrl === 'string' && nextUrl ? nextUrl : null;
  }

  #showPanelError() {
    const { panelLoading, panelContent, panelError } = this.refs;

    if (panelLoading instanceof HTMLElement) panelLoading.hidden = true;
    if (panelContent instanceof HTMLElement) panelContent.hidden = true;
    if (panelError instanceof HTMLElement) panelError.hidden = false;
  }

  /**
   * @param {string} userId
   * @param {string} productId
   * @param {string} path
   */
  async #fetchPath(userId, productId, path) {
    const url = `${this.#apiBase}/products/shopify-${productId}/${path}`;
    return this.#fetchUrl(url);
  }

  /**
   * @param {string} url
   */
  async #fetchUrl(url) {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Okendo API ${response.status}`);
    }

    return response.json();
  }

  /**
   * @param {unknown} data
   */
  #extractSummaryText(data) {
    if (!data || typeof data !== 'object') return '';

    const record = /** @type {Record<string, unknown>} */ (data);
    const reviewsSummary = record.reviewsSummary;

    if (reviewsSummary && typeof reviewsSummary === 'object') {
      const body = /** @type {{ body?: string }} */ (reviewsSummary).body;
      if (body) return body;
    }

    const summary = record.summary;

    if (summary && typeof summary === 'object') {
      const text = /** @type {{ text?: string; summary?: string; body?: string }} */ (summary).body
        ?? /** @type {{ text?: string; summary?: string; body?: string }} */ (summary).text
        ?? /** @type {{ text?: string; summary?: string; body?: string }} */ (summary).summary;
      if (text) return text;
    }

    if (typeof record.text === 'string') return record.text;
    if (typeof record.summary === 'string') return record.summary;
    if (typeof record.reviewSummary === 'string') return record.reviewSummary;
    if (typeof record.aiSummary === 'string') return record.aiSummary;

    return '';
  }

  #buildFallbackSummary() {
    const count = Number(this.dataset.ratingCount);
    if (!count) return '';
    return 'Read what customers are saying about fit, quality, and design.';
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #buildRecommendText(aggregate) {
    const count = this.#getAggregateCount(aggregate);
    if (!count) return this.#buildFallbackRecommendText();

    const recommendPct = Number(
      aggregate?.recommendationPercentage ?? aggregate?.recommendedPercentage ?? aggregate?.recommendPercent
    );
    if (recommendPct > 0) {
      return `${Math.round(recommendPct)}% would recommend this product`;
    }

    const recommendCount = Number(aggregate?.recommendationCount ?? 0);
    if (recommendCount > 0) {
      return `${Math.round((recommendCount / count) * 100)}% would recommend this product`;
    }

    return this.#buildFallbackRecommendText();
  }

  #buildFallbackRecommendText() {
    return '';
  }

  /**
   * @param {string} summaryText
   */
  #setSummaryText(summaryText) {
    const { summaryText: summaryEl } = this.refs;
    if (!(summaryEl instanceof HTMLElement)) return;

    const text = String(summaryText ?? '').trim();
    if (!text) return;

    summaryEl.textContent = text;
    summaryEl.hidden = false;
  }

  /**
   * @param {string} recommendText
   */
  #setSummaryRecommend(recommendText) {
    const { summaryRecommend } = this.refs;
    if (!(summaryRecommend instanceof HTMLElement)) return;

    const text = String(recommendText ?? '').trim();
    if (!text) return;

    summaryRecommend.textContent = text;
    summaryRecommend.hidden = false;
  }

  /**
   * @param {unknown} data
   */
  #extractAggregate(data) {
    if (!data || typeof data !== 'object') {
      return this.#aggregateFromDataset();
    }

    const record = /** @type {Record<string, unknown>} */ (data);
    const aggregate = record.reviewAggregate ?? record.productReviewAggregate ?? record.aggregate ?? record;

    if (!aggregate || typeof aggregate !== 'object') {
      return this.#aggregateFromDataset();
    }

    return /** @type {Record<string, unknown>} */ (aggregate);
  }

  #aggregateFromDataset() {
    const rating = Number(this.dataset.rating);
    const count = Number(this.dataset.ratingCount);

    if (!rating || !count) return null;

    return {
      ratingValue: rating,
      reviewCount: count,
      ratingCount: count,
      ratingAndReviewCount: count,
    };
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #getAggregateRating(aggregate) {
    const directRating = Number(
      aggregate?.ratingValue ?? aggregate?.averageRating ?? aggregate?.rating ?? this.dataset.rating
    );

    if (directRating > 0) return directRating;

    const total = Number(aggregate?.reviewRatingValuesTotal ?? aggregate?.ratingAndReviewValuesTotal ?? 0);
    const count = Number(aggregate?.reviewCount ?? aggregate?.ratingAndReviewCount ?? this.dataset.ratingCount ?? 0);

    if (total > 0 && count > 0) {
      return total / count;
    }

    return Number(this.dataset.rating) || 0;
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #getAggregateCount(aggregate) {
    return Number(
      aggregate?.reviewCount
        ?? aggregate?.ratingCount
        ?? aggregate?.ratingAndReviewCount
        ?? this.dataset.ratingCount
        ?? 0
    );
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #renderAggregate(aggregate) {
    const { aggregateBlock, aggregateStars, aggregateMeta, recommendText } = this.refs;

    if (!(aggregateBlock instanceof HTMLElement)) return;

    const rating = this.#getAggregateRating(aggregate);
    const count = this.#getAggregateCount(aggregate);

    if (!rating || !count) {
      aggregateBlock.hidden = true;
      return;
    }

    if (aggregateStars instanceof HTMLElement) {
      aggregateStars.innerHTML = this.#renderStarsHtml(rating, 14);
    }

    if (aggregateMeta instanceof HTMLElement) {
      aggregateMeta.textContent = `${rating.toFixed(1)} · Based on ${count} reviews`;
    }

    const recommendCount = Number(aggregate?.recommendationCount ?? 0);
    const recommendPct = Number(
      aggregate?.recommendationPercentage ?? aggregate?.recommendedPercentage ?? aggregate?.recommendPercent
    );

    if (recommendText instanceof HTMLElement) {
      if (recommendPct > 0) {
        recommendText.textContent = `${Math.round(recommendPct)}% would recommend this product`;
        recommendText.hidden = false;
      } else if (recommendCount > 0 && count > 0) {
        recommendText.textContent = `${Math.round((recommendCount / count) * 100)}% would recommend this product`;
        recommendText.hidden = false;
      }
    }

    aggregateBlock.hidden = false;
  }

  /**
   * @param {Record<string, unknown> | null} aggregate
   */
  #renderAttributes(aggregate) {
    const { attributesBlock, attributeBars } = this.refs;

    if (!(attributesBlock instanceof HTMLElement) || !(attributeBars instanceof HTMLElement) || !aggregate) {
      return;
    }

    const attributes = this.#sortReviewAttributes(
      aggregate.attributeRatings ?? aggregate.reviewAttributes ?? aggregate.attributes ?? aggregate.productAttributes ?? []
    );

    if (!Array.isArray(attributes) || attributes.length === 0) {
      attributesBlock.hidden = true;
      return;
    }

    const rows = attributes
      .map((attr) => {
        if (!attr || typeof attr !== 'object') return '';

        const item = /** @type {Record<string, unknown>} */ (attr);
        return this.#renderAttributeRow(item);
      })
      .filter(Boolean)
      .join('');

    if (!rows) {
      attributesBlock.hidden = true;
      return;
    }

    attributeBars.innerHTML = rows;
    attributesBlock.hidden = false;
  }

  /**
   * @param {Record<string, unknown>} item
   */
  #renderAttributeRow(item) {
    const label = String(item.title ?? item.name ?? item.label ?? '');
    if (!label) return '';

    const type = String(item.type ?? '');
    const value = Number(item.value ?? item.average ?? item.score ?? 0);

    if (type === 'centered-range') {
      return this.#renderCenteredRangeAttributeRow(label, item, value);
    }

    const max = Number(item.max ?? item.scaleMax ?? 5);
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;

    return `
      <div class="product-reviews__bar-row product-reviews__attribute-row">
        <span class="product-reviews__bar-label">${this.#escapeHtml(label)}</span>
        <span class="product-reviews__bar-track" aria-hidden="true">
          <span class="product-reviews__bar-fill" style="width: ${pct}%"></span>
        </span>
        <span class="product-reviews__bar-value">${value.toFixed(1)}</span>
      </div>
    `;
  }

  /**
   * @param {string} label
   * @param {Record<string, unknown>} item
   * @param {number} value
   */
  #renderCenteredRangeAttributeRow(label, item, value) {
    const max = Number(item.max ?? item.scaleMax ?? 2);
    const markerPct = Math.max(0, Math.min(100, ((value + max) / (max * 2)) * 100));
    const centerPct = 50;
    const minLabel = this.#formatRangeEndpointLabel(item.minLabel, 'Runs Small');
    const midLabel = String(item.midLabel ?? 'True to Size').trim() || 'True to Size';
    const maxLabel = this.#formatRangeEndpointLabel(item.maxLabel, 'Runs Large');
    const summaryLabel = this.#describeCenteredRangeValue(value, minLabel, midLabel, maxLabel);
    const connectorLeft = Math.min(centerPct, markerPct);
    const connectorWidth = Math.abs(markerPct - centerPct);
    const showConnector = connectorWidth >= 1.5;
    const scaleDots = [0, 25, 50, 75, 100];

    return `
      <div class="product-reviews__bar-row product-reviews__attribute-row product-reviews__bar-row--centered-range">
        <span class="product-reviews__bar-label">${this.#escapeHtml(label)}</span>
        <div class="product-reviews__range-chart" role="img" aria-label="${this.#escapeHtml(`${label}: ${summaryLabel}`)}">
          <div class="product-reviews__range-scale" aria-hidden="true">
            <span class="product-reviews__range-line"></span>
            ${scaleDots
              .map(
                (dotPct) =>
                  `<span class="product-reviews__range-scale-dot" style="left: ${dotPct}%"></span>`
              )
              .join('')}
            <span class="product-reviews__range-center-bar"></span>
            ${
              showConnector
                ? `<span class="product-reviews__range-connector" style="left: ${connectorLeft}%; width: ${connectorWidth}%"></span>`
                : ''
            }
            <span class="product-reviews__range-fit-dot" style="left: ${markerPct}%"></span>
          </div>
          <div class="product-reviews__range-labels" aria-hidden="true">
            <span>${this.#escapeHtml(minLabel)}</span>
            <span>${this.#escapeHtml(midLabel)}</span>
            <span>${this.#escapeHtml(maxLabel)}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * @param {unknown} label
   * @param {string} fallback
   */
  #formatRangeEndpointLabel(label, fallback) {
    const text = String(label ?? '').trim();
    return text || fallback;
  }

  /**
   * @param {number} value
   * @param {string} minLabel
   * @param {string} midLabel
   * @param {string} maxLabel
   */
  #describeCenteredRangeValue(value, minLabel, midLabel, maxLabel) {
    const abs = Math.abs(value);

    if (abs < 0.2) return midLabel;
    if (value <= -0.75) return minLabel;
    if (value <= -0.2) return `Slightly ${minLabel.toLowerCase()}`;
    if (value >= 0.75) return maxLabel;
    if (value >= 0.2) return `Slightly ${maxLabel.toLowerCase()}`;

    return midLabel;
  }

  /**
   * @param {unknown[]} reviews
   */
  #renderReviews(reviews) {
    this.#allReviews = reviews
      .filter((review) => review && typeof review === 'object')
      .map((review) => /** @type {Record<string, unknown>} */ (review));
    this.#renderReviewList();
  }

  /**
   * @param {unknown[]} reviews
   */
  #appendReviews(reviews) {
    if (reviews.length === 0) return;

    const existingIds = new Set(
      this.#allReviews
        .map((review) => this.#getReviewId(review))
        .filter(Boolean)
    );

    const mapped = reviews
      .filter((review) => review && typeof review === 'object')
      .map((review) => /** @type {Record<string, unknown>} */ (review))
      .filter((review) => {
        const id = this.#getReviewId(review);
        return !id || !existingIds.has(id);
      });

    if (mapped.length === 0) return;

    this.#allReviews = [...this.#allReviews, ...mapped];
    this.#renderReviewList();
  }

  /**
   * @param {Record<string, unknown>} review
   */
  #getReviewId(review) {
    const id = review.reviewId ?? review.id;
    return id ? String(id) : '';
  }

  #renderReviewList() {
    const { reviewsList, emptyState } = this.refs;
    if (!(reviewsList instanceof HTMLElement)) return;

    reviewsList.innerHTML = this.#allReviews.map((review) => this.#renderReviewCard(review)).join('');

    if (emptyState instanceof HTMLElement) {
      emptyState.hidden = this.#allReviews.length > 0;
    }
  }

  /**
   * @param {unknown} review
   */
  #renderReviewCard(review) {
    if (!review || typeof review !== 'object') return '';

    const item = /** @type {Record<string, unknown>} */ (review);
    const rating = Number(item.rating ?? item.reviewRating ?? 0);
    const title = String(item.title ?? '');
    const body = String(item.body ?? item.reviewBody ?? item.content ?? '');
    const date = this.#formatDate(item.dateCreated ?? item.createdAt ?? item.date);
    const reviewer = item.reviewer && typeof item.reviewer === 'object'
      ? /** @type {Record<string, unknown>} */ (item.reviewer)
      : {};
    const name = String(reviewer.displayName ?? reviewer.name ?? item.reviewerName ?? 'Customer');
    const isVerified = Boolean(reviewer.isVerified ?? item.isVerified ?? item.verifiedBuyer);
    const media = Array.isArray(item.media) ? item.media : [];
    const reviewerAttributes = Array.isArray(reviewer.attributes) ? reviewer.attributes : [];
    const productAttributes = Array.isArray(item.productAttributes) ? item.productAttributes : [];

    const metaParts = [...reviewerAttributes, ...productAttributes]
      .map((attr) => this.#formatReviewAttributeMeta(attr))
      .filter(Boolean);

    const mediaHtml = media
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return '';
        const m = /** @type {Record<string, unknown>} */ (entry);
        const url = String(m.fullSizeUrl ?? m.largeUrl ?? m.url ?? m.thumbnailUrl ?? '');
        if (!url) return '';
        const alt = this.#escapeHtml(String(m.alt ?? 'Review photo'));
        return `<img src="${this.#escapeHtml(url)}" alt="${alt}" loading="lazy" width="72" height="72">`;
      })
      .filter(Boolean)
      .join('');

    const verifiedHtml = isVerified && this.#verifiedIconHtml
      ? `<span class="product-reviews__card-verified" aria-label="Verified buyer">${this.#verifiedIconHtml}</span>`
      : '';
    const dateHtml = date
      ? `<time class="product-reviews__card-date">${this.#escapeHtml(date)}</time>`
      : '';

    return `
      <article class="product-reviews__card">
        ${title ? `<h3 class="product-reviews__card-title">${this.#escapeHtml(title)}</h3>` : ''}
        <div class="product-reviews__card-header">
          <div class="product-reviews__card-stars" aria-label="${rating} out of 5 stars">
            ${this.#renderStarsHtml(rating, 11)}
          </div>
          <div class="product-reviews__card-byline">
            <span class="product-reviews__card-name">${this.#escapeHtml(name)}</span>
            ${verifiedHtml || dateHtml ? `<span class="product-reviews__card-badges">${verifiedHtml}${dateHtml}</span>` : ''}
          </div>
        </div>
        ${metaParts.length ? `<p class="product-reviews__card-meta">${this.#escapeHtml(metaParts.join(' · '))}</p>` : ''}
        ${body ? `<p class="product-reviews__card-body">${this.#escapeHtml(body)}</p>` : ''}
        ${mediaHtml ? `<div class="product-reviews__card-media">${mediaHtml}</div>` : ''}
      </article>
    `;
  }

  /**
   * @param {number} rating
   * @param {number} size
   */
  #renderStarsHtml(rating, size) {
    const filled = Math.round(rating);
    const starPath =
      'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

    return Array.from({ length: 5 }, (_, index) => {
      const starNumber = index + 1;
      const fill = starNumber <= filled ? 'var(--product-reviews-star-color)' : 'rgb(from var(--product-reviews-star-color) r g b / 0.2)';
      return `<svg class="product-reviews__card-star" style="width:${size}px;height:${size}px" viewBox="0 0 32 32" role="presentation"><path fill="${fill}" d="${starPath}"></path></svg>`;
    }).join('');
  }

  #updateLoadMore() {
    const { loadMoreWrap, loadMoreButton } = this.refs;
    const hasMore = Boolean(this.#nextUrl);

    if (loadMoreWrap instanceof HTMLElement) {
      loadMoreWrap.hidden = !hasMore;
    }

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.disabled = !hasMore || this.#loadingMore || this.#loadingReviews;
    }
  }

  #updateFiltersButton() {
    const { filtersToggle } = this.refs;
    if (!(filtersToggle instanceof HTMLElement)) return;

    let activeCount = 0;
    if (this.#filters.rating) activeCount += 1;
    if (this.#filters.shopperAttribute) activeCount += 1;
    if (this.#filters.keyword) activeCount += 1;

    filtersToggle.textContent = activeCount > 0 ? `FILTERS (${activeCount})` : 'FILTERS';
  }

  /**
   * @param {unknown} value
   */
  #formatDate(value) {
    if (!value) return '';

    const date = new Date(String(value));

    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /**
   * @param {unknown} attr
   */
  #formatReviewAttributeMeta(attr) {
    if (!attr || typeof attr !== 'object') return '';

    const item = /** @type {Record<string, unknown>} */ (attr);
    const label = String(item.title ?? item.name ?? item.label ?? '').trim();
    if (!label) return '';

    const type = String(item.type ?? '');
    const numericValue = Number(item.value ?? item.average ?? item.score);

    if (type === 'centered-range' && !Number.isNaN(numericValue)) {
      const minLabel = this.#formatRangeEndpointLabel(item.minLabel, 'Runs Small');
      const midLabel = String(item.midLabel ?? 'True to Size').trim() || 'True to Size';
      const maxLabel = this.#formatRangeEndpointLabel(item.maxLabel, 'Runs Large');
      return `${label}: ${this.#describeCenteredRangeValue(numericValue, minLabel, midLabel, maxLabel)}`;
    }

    const value = String(item.value ?? item.valueLabel ?? '').trim();
    if (!value) return '';

    return `${label}: ${value}`;
  }

  /**
   * @param {unknown} attributes
   */
  #sortReviewAttributes(attributes) {
    if (!Array.isArray(attributes)) return [];

    return [...attributes].sort((left, right) => {
      return this.#getReviewAttributeSortIndex(left) - this.#getReviewAttributeSortIndex(right);
    });
  }

  /**
   * @param {unknown} attribute
   */
  #getReviewAttributeSortIndex(attribute) {
    if (!attribute || typeof attribute !== 'object') {
      return REVIEW_ATTRIBUTE_ORDER.length;
    }

    const item = /** @type {Record<string, unknown>} */ (attribute);
    const title = String(item.title ?? item.name ?? item.label ?? item.groupTitle ?? '').trim().toLowerCase();

    if (!title) return REVIEW_ATTRIBUTE_ORDER.length;

    const exactIndex = REVIEW_ATTRIBUTE_ORDER.indexOf(title);
    if (exactIndex !== -1) return exactIndex;

    for (let index = 0; index < REVIEW_ATTRIBUTE_ORDER.length; index += 1) {
      if (title.includes(REVIEW_ATTRIBUTE_ORDER[index])) return index;
    }

    return REVIEW_ATTRIBUTE_ORDER.length;
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('product-reviews-component')) {
  customElements.define('product-reviews-component', ProductReviewsComponent);
}
